/**
 * AI Coach Chat Hook - Using OpenAI's Canonical runTools Approach
 */

import { zodFunction } from 'openai/helpers/zod'
import type { ChatCompletionMessageParam } from 'openai/resources/chat/completions'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { useUserCredentials } from '../api/hooks/useCredentials'
import { useExerciseTemplates } from '../api/hooks/useExerciseTemplates'
import { useRoutines } from '../api/hooks/useRoutines'
import { useUpdateAppSettings, useUserSettings } from '../api/hooks/useSettings'
import { useWithingsWeightData } from '../api/hooks/useWithingsWeight'
import { useWorkoutData } from '../api/hooks/useWorkoutData'
import { toolRegistry, type ToolContext } from '../lib/ai-tools'
import { useOpenAIStore } from '../stores/openAIStore'
import type { AppSettings } from '../types'

// Default model configuration
const DEFAULT_AI_MODEL = 'gpt-4.1'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  tool_call_id?: string
  tool_calls?: Array<{
    id: string
    type: 'function'
    function: {
      name: string
      arguments: string
    }
  }>
}

// Helper function to get natural language loading message for each tool
const getToolLoadingMessage = (toolName: string): string => {
  const toolMessages: Record<string, string> = {
    getOneRMTrend: 'Looking at your strength progression...',
    getBodyweightTrend: 'Checking your weight trends...',
    getRecentWorkouts: 'Reviewing your recent training sessions...',
    getUserPerformanceMetrics: 'Analyzing your overall performance...',
    getCurrentSplit: 'Looking at your current training split...',
    lookupExerciseTemplateID: 'Finding that exercise for you...',
    createRoutine: 'Building your workout routine...',
    createRoutineFolder: 'Organizing your training programs...',
    generateGraph: 'Creating a visual for you...',
    getCurrentAppStateTool: 'Checking your app settings...',
    getAppState: 'Checking your app settings...',
    updateAiSettings: 'Updating your preferences...'
  }

  return toolMessages[toolName] || 'Looking over your data...'
}

export const useCoachChat = () => {
  const apiKey = useOpenAIStore((state) => state.apiKey)
  const setQuotaExhausted = useOpenAIStore((state) => state.setQuotaExhausted)
  const { data: userSettings } = useUserSettings()
  const updateAppSettings = useUpdateAppSettings()
  const workoutData = useWorkoutData()
  const { data: routines } = useRoutines()
  const { exercises: exerciseTemplates } = useExerciseTemplates()
  const { data: weightData } = useWithingsWeightData()
  const { data: userCredentials } = useUserCredentials()

  // Initialize model from user settings or default
  const savedModel = userSettings?.settings?.ai?.model || DEFAULT_AI_MODEL
  const [aiModel, setAiModel] = useState<'gpt-4.1' | 'o1'>(savedModel as 'gpt-4.1' | 'o1')

  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [toolStatus, setToolStatus] = useState<string>('')
  const abortControllerRef = useRef<AbortController | null>(null)

  // Get AI settings from user settings
  const personality = userSettings?.settings?.ai?.personality || 'supportive'
  const trainingExperience = userSettings?.settings?.ai?.training_experience || 'intermediate'
  const userGoal = userSettings?.settings?.ai?.user_goal || 'hypertrophy'

  // Update model when user settings change
  useEffect(() => {
    if (userSettings?.settings?.ai?.model && userSettings.settings.ai.model !== aiModel) {
      setAiModel(userSettings.settings.ai.model)
    }
  }, [userSettings?.settings?.ai?.model, aiModel])

  // Save model to user settings when it changes
  const setModelWithSave = useCallback(
    (model: 'gpt-4.1' | 'o1') => {
      setAiModel(model)
      // Save to user settings
      if (userSettings?.settings) {
        const currentAi = userSettings.settings.ai || {
          personality: 'supportive',
          training_experience: 'intermediate'
        }
        const updatedSettings: Partial<AppSettings> = {
          ai: {
            personality: currentAi.personality || 'supportive',
            training_experience: currentAi.training_experience || 'intermediate',
            user_goal: currentAi.user_goal,
            model
          }
        }
        updateAppSettings.mutate(updatedSettings)
      }
    },
    [userSettings?.settings, updateAppSettings]
  )

  // Calculate performance metrics for system prompt
  const performanceMetrics = useMemo(() => {
    if (!workoutData.workouts?.length) return null

    const stats = workoutData.weeklyStats
    const recentWorkouts = workoutData.workouts.slice(0, 5)
    const topExercises = workoutData.oneRepMaxes?.slice(0, 5) || []

    // Calculate 28-day metrics
    const now = new Date()
    const twentyEightDaysAgo = new Date(now.getTime() - 27 * 24 * 60 * 60 * 1000)
    const last28DaysWorkouts = workoutData.workouts.filter(
      (w) => new Date(w.start_time) >= twentyEightDaysAgo
    )

    // Calculate consistency
    const targetPerWeek = Number(userSettings?.settings?.algorithms?.targetWorkoutsPerWeek) || 3
    const targetFor28Days = Math.round((targetPerWeek / 7) * 28)
    const consistencyRate =
      targetFor28Days > 0
        ? Math.min(100, Math.round((last28DaysWorkouts.length / targetFor28Days) * 100))
        : 0

    // Active days calculation
    const workoutDates = new Set(
      last28DaysWorkouts.map((w) => new Date(w.start_time).toISOString().split('T')[0])
    )
    const activeDays = workoutDates.size
    const restDays = 28 - activeDays

    // Last 7 days sessions
    const sevenDaysAgo = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000)
    const sessionsLast7Days = workoutData.workouts.filter(
      (w) => new Date(w.start_time) >= sevenDaysAgo
    ).length

    // Extract routines
    const routineMap = new Map<string, number>()
    last28DaysWorkouts.forEach((workout) => {
      const routineName = workout.title || 'Untitled Workout'
      routineMap.set(routineName, (routineMap.get(routineName) || 0) + 1)
    })

    const routinesLast28Days = Array.from(routineMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)

    return {
      consistencyRate: `${consistencyRate}%`,
      targetSessionsAWeek: targetPerWeek,
      sessionsLast7days: sessionsLast7Days,
      activeDaysLast28Days: activeDays,
      restDaysLast28Days: restDays,
      avgSessionsAWeekLast28Days: Math.round((last28DaysWorkouts.length / 4) * 10) / 10,
      routinesLast28Days: routinesLast28Days,
      totalWorkouts: workoutData.workouts.length,
      thisWeekSets: stats?.totalPhysicalSets || 0,
      lastWeekSets: stats?.previousWeek?.totalPhysicalSets || 0,
      topExercises: topExercises.map((e) => `${e.name} (${e.currentMax}kg)`),
      recentSessions: recentWorkouts.map((w) => new Date(w.start_time).toLocaleDateString())
    }
  }, [workoutData, userSettings])

  // Build system prompt
  const systemPrompt = useMemo(() => {
    if (!performanceMetrics) return ''

    return `You are Milo, an AI fitness coach.

CRITICAL: Most user questions require tools. Vague questions especially need context from tools first.

TODAY'S DATE: ${new Date().toLocaleDateString()}
STYLE: ${personality}
USER: ${trainingExperience}, goal: ${userGoal}`
  }, [personality, trainingExperience, userGoal, performanceMetrics])

  // Send message using OpenAI's canonical runTools approach
  const sendMessage = useCallback(
    async (userMessage: string, currentMessages: Message[]) => {
      if (!apiKey) {
        toast.error('Please configure your OpenAI API key')
        return
      }

      const newUserMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: userMessage
      }

      // Update UI with user message
      const updatedMessages = [...currentMessages, newUserMessage]
      setMessages(updatedMessages)
      setInput('')
      setIsLoading(true)

      // Create abort controller for cancellation
      abortControllerRef.current = new AbortController()

      try {
        // Dynamically import OpenAI to avoid bundling issues
        const { default: OpenAI } = await import('openai')

        const openai = new OpenAI({
          apiKey,
          dangerouslyAllowBrowser: true,
          maxRetries: 3,
          timeout: 30 * 1000
        })

        // Create tool context
        const toolContext: ToolContext = {
          workoutData,
          userSettings: userSettings || null,
          routines: routines || undefined,
          exerciseTemplates: exerciseTemplates || undefined,
          weightData: weightData || undefined,
          userCredentials: userCredentials || undefined
        }

        // Create tools using OpenAI's canonical zodFunction approach
        const tools = toolRegistry.getTools().map((tool) => {
          return zodFunction({
            name: tool.name,
            description: tool.description,
            parameters: tool.parameters,
            function: async (args: z.infer<typeof tool.parameters>) => {
              try {
                const result = await tool.execute(args, toolContext)
                return result
              } catch (error) {
                console.error(`Tool ${tool.name} error:`, error)
                return { error: error instanceof Error ? error.message : 'Tool execution failed' }
              }
            }
          })
        })

        // Convert messages to OpenAI format
        const apiMessages: ChatCompletionMessageParam[] = currentMessages.map((msg) => {
          if (msg.role === 'tool' && msg.tool_call_id) {
            return {
              role: 'tool',
              content: msg.content,
              tool_call_id: msg.tool_call_id
            }
          }
          if (msg.role === 'assistant' && msg.tool_calls) {
            return {
              role: 'assistant',
              content: msg.content || '',
              tool_calls: msg.tool_calls
            }
          }
          return {
            role: msg.role as 'user' | 'assistant' | 'system',
            content: msg.content
          }
        })

        // Add system prompt
        if (aiModel === 'o1') {
          // o1 models don't support system messages
          apiMessages.unshift({
            role: 'user',
            content: `Context: ${systemPrompt}\n\nUser: ${userMessage}`
          })
        } else {
          apiMessages.unshift({ role: 'system', content: systemPrompt })
          apiMessages.push({ role: 'user', content: userMessage })
        }

        // Use OpenAI's canonical runTools approach for automatic tool execution
        const runner =
          tools.length > 0
            ? openai.chat.completions.runTools(
                {
                  model: aiModel,
                  messages: apiMessages,
                  tools: tools,
                  tool_choice: 'auto',
                  stream: true
                },
                {
                  signal: abortControllerRef.current?.signal
                }
              )
            : openai.chat.completions.stream(
                {
                  model: aiModel,
                  messages: apiMessages,
                  stream: true
                },
                {
                  signal: abortControllerRef.current?.signal
                }
              )

        // Set up tool execution event listeners for loading states
        if (tools.length > 0) {
          runner
            .on('functionToolCall', (functionCall) => {
              // Set loading state when tool is called
              const toolName = functionCall.name
              setToolStatus(getToolLoadingMessage(toolName))
            })
            .on('functionToolCallResult', (_functionCallResult) => {
              // Clear loading state when tool returns result
              setToolStatus('')
            })
        }

        // Create empty assistant message immediately to show bobbing dots
        const assistantMessageId = Date.now().toString()
        setMessages((prev) => [
          ...prev,
          {
            id: assistantMessageId,
            role: 'assistant',
            content: ''
          }
        ])

        // Track the assistant message
        let assistantMessage = ''

        // Handle streaming chunks - runTools handles tool execution automatically
        for await (const chunk of runner) {
          const delta = chunk.choices[0]?.delta

          if (delta?.content) {
            assistantMessage += delta.content
            // Update the last message in real-time
            setMessages((prev) => {
              const newMessages = [...prev]
              const lastMessage = newMessages[newMessages.length - 1]
              if (lastMessage && lastMessage.role === 'assistant') {
                lastMessage.content = assistantMessage
              }
              return newMessages
            })
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Request was cancelled
          toast.info('Message cancelled')
        } else {
          console.error('Chat error:', error)

          // Handle quota exhaustion
          if (
            error &&
            typeof error === 'object' &&
            'code' in error &&
            error.code === 'insufficient_quota'
          ) {
            setQuotaExhausted(true)
            toast.error(
              'Your OpenAI API key has run out of credits. Please add credits to continue.'
            )
          } else {
            toast.error('Failed to send message. Please check your API key.')
          }
        }
      } finally {
        setIsLoading(false)
        setToolStatus('')
        abortControllerRef.current = null
      }
    },
    [
      apiKey,
      systemPrompt,
      workoutData,
      userSettings,
      routines,
      exerciseTemplates,
      weightData,
      userCredentials,
      aiModel,
      setQuotaExhausted
    ]
  )

  // Handle form submission
  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      if (!input.trim()) return

      if (!workoutData.workouts?.length) {
        toast.error('No workout data available')
        return
      }

      sendMessage(input, messages)
    },
    [input, workoutData, sendMessage, messages]
  )

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value)
  }, [])

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  // Stop generation
  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    setToolStatus('') // Clear tool status
  }, [])

  // Check if everything is ready
  const isReady = !!apiKey && !!workoutData.workouts?.length

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    toolStatus,
    error: null,
    clearMessages,
    stop,
    reload: () => {}, // Not implemented
    isReady,
    setModel: setModelWithSave,
    currentModel: aiModel
  }
}
